import { create } from "zustand";
import {
  acknowledgeAlert as acknowledgeAlertApi,
  bulkAlertAction as bulkAlertActionApi,
  dismissAlert as dismissAlertApi,
  getAlertCount as getAlertCountApi,
  getAlertHistory as getAlertHistoryApi,
  listAlerts as listAlertsApi,
} from "../services/alertService";
import type {
  AlertCountResponse,
  AlertHistoryItem,
  AlertWithContext,
} from "../types/models";

export interface AlertListParams {
  page?: number;
  limit?: number;
  status?: string | null;
  priority?: string | null;
}

export interface AlertHistoryParams {
  page?: number;
  limit?: number;
  status?: string | null;
  date_from?: string | null;
  date_to?: string | null;
}

interface ReorderAlertState {
  alerts: AlertWithContext[];
  loading: boolean;
  error: string | null;
  pagination: { total: number; page: number; limit: number; pages: number };
  alertCount: AlertCountResponse | null;
  alertCountError: string | null;
  statusFilter: string | null;
  priorityFilter: string | null;
  historyItems: AlertHistoryItem[];
  historyLoading: boolean;
  historyError: string | null;
  historyPagination: { total: number; page: number; limit: number; pages: number };
  historyStatusFilter: string | null;
  historyDateFrom: string | null;
  historyDateTo: string | null;
  fetchAlerts: (params?: AlertListParams) => Promise<void>;
  fetchAlertCount: () => Promise<void>;
  fetchAlertHistory: (params?: AlertHistoryParams) => Promise<void>;
  setHistoryPage: (page: number) => void;
  setHistoryFilters: (filters: {
    status?: string | null;
    date_from?: string | null;
    date_to?: string | null;
  }) => void;
  acknowledgeAlert: (alertId: string) => Promise<void>;
  dismissAlert: (alertId: string) => Promise<void>;
  bulkAcknowledge: (alertIds: string[]) => Promise<void>;
  bulkDismiss: (alertIds: string[]) => Promise<void>;
  selectedAlertIds: Set<string>;
  setSelectedAlertIds: (ids: Set<string>) => void;
  toggleAlertSelection: (id: string) => void;
  setPage: (page: number) => void;
  setFilters: (filters: { status?: string | null; priority?: string | null }) => void;
  clearError: () => void;
  clearAlertCountError: () => void;
  bulkActionError: string | null;
  clearBulkActionError: () => void;
}

export const useReorderAlertStore = create<ReorderAlertState>((set, get) => ({
  alerts: [],
  loading: false,
  error: null,
  pagination: { total: 0, page: 1, limit: 20, pages: 0 },
  alertCount: null,
  alertCountError: null,
  statusFilter: "active",
  priorityFilter: null,
  historyItems: [],
  historyLoading: false,
  historyError: null,
  historyPagination: { total: 0, page: 1, limit: 20, pages: 0 },
  historyStatusFilter: null,
  historyDateFrom: null,
  historyDateTo: null,
  selectedAlertIds: new Set<string>(),

  setSelectedAlertIds: (ids) => set({ selectedAlertIds: ids }),
  toggleAlertSelection: (id) => {
    set((s) => {
      const next = new Set(s.selectedAlertIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { selectedAlertIds: next };
    });
  },

  clearError: () => set({ error: null }),
  clearAlertCountError: () => set({ alertCountError: null }),
  bulkActionError: null,
  clearBulkActionError: () => set({ bulkActionError: null }),


  setFilters: (filters) => {
    const { statusFilter, priorityFilter } = get();
    const nextStatus = filters.status !== undefined ? filters.status : statusFilter;
    const nextPriority = filters.priority !== undefined ? filters.priority : priorityFilter;
    set({ statusFilter: nextStatus, priorityFilter: nextPriority });
    get().fetchAlerts({ page: 1, status: nextStatus, priority: nextPriority });
  },

  fetchAlertCount: async () => {
    set({ alertCountError: null });
    try {
      const data = await getAlertCountApi();
      set({ alertCount: data, alertCountError: null });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Failed to load alert count";
      set({ alertCount: null, alertCountError: message });
    }
  },

  setPage: (page) => {
    set((s) => ({ pagination: { ...s.pagination, page } }));
    const { pagination, statusFilter, priorityFilter } = get();
    get().fetchAlerts({
      page,
      limit: pagination.limit,
      status: statusFilter,
      priority: priorityFilter,
    });
  },

  fetchAlerts: async (params) => {
    set({ loading: true, error: null });
    const { pagination, statusFilter, priorityFilter } = get();
    const page = params?.page ?? pagination.page;
    const limit = params?.limit ?? pagination.limit;
    const status = params?.status ?? statusFilter;
    const priority = params?.priority ?? priorityFilter;
    try {
      const data = await listAlertsApi({ page, limit, status, priority });
      set({
        alerts: data.items,
        pagination: { total: data.total, page: data.page, limit: data.limit, pages: data.pages },
        statusFilter: status ?? null,
        priorityFilter: priority ?? null,
        loading: false,
        error: null,
      });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Failed to load alerts";
      set({ loading: false, error: message });
    }
  },

  acknowledgeAlert: async (alertId) => {
    await acknowledgeAlertApi(alertId);
    const { pagination, statusFilter, priorityFilter } = get();
    await get().fetchAlerts({ page: pagination.page, limit: pagination.limit, status: statusFilter, priority: priorityFilter });
    await get().fetchAlertCount();
  },

  dismissAlert: async (alertId) => {
    await dismissAlertApi(alertId);
    const { pagination, statusFilter, priorityFilter } = get();
    await get().fetchAlerts({ page: pagination.page, limit: pagination.limit, status: statusFilter, priority: priorityFilter });
    await get().fetchAlertCount();
  },

  bulkAcknowledge: async (alertIds) => {
    if (alertIds.length === 0) return;
    set({ bulkActionError: null });
    try {
      await bulkAlertActionApi({ action: "acknowledge", alert_ids: alertIds });
      set({ selectedAlertIds: new Set<string>(), bulkActionError: null });
      const { pagination, statusFilter, priorityFilter } = get();
      await get().fetchAlerts({ page: pagination.page, limit: pagination.limit, status: statusFilter, priority: priorityFilter });
      await get().fetchAlertCount();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Failed to acknowledge selected alerts";
      set({ bulkActionError: message });
    }
  },

  bulkDismiss: async (alertIds) => {
    if (alertIds.length === 0) return;
    set({ bulkActionError: null });
    try {
      await bulkAlertActionApi({ action: "dismiss", alert_ids: alertIds });
      set({ selectedAlertIds: new Set<string>(), bulkActionError: null });
      const { pagination, statusFilter, priorityFilter } = get();
      await get().fetchAlerts({ page: pagination.page, limit: pagination.limit, status: statusFilter, priority: priorityFilter });
      await get().fetchAlertCount();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Failed to dismiss selected alerts";
      set({ bulkActionError: message });
    }
  },

  fetchAlertHistory: async (params) => {
    set({ historyLoading: true, historyError: null });
    const {
      historyPagination,
      historyStatusFilter,
      historyDateFrom,
      historyDateTo,
    } = get();
    const page = params?.page ?? historyPagination.page;
    const limit = params?.limit ?? historyPagination.limit;
    const status = params?.status ?? historyStatusFilter;
    const date_from = params?.date_from ?? historyDateFrom;
    const date_to = params?.date_to ?? historyDateTo;
    try {
      const data = await getAlertHistoryApi({ page, limit, status, date_from, date_to });
      set({
        historyItems: data.items,
        historyPagination: { total: data.total, page: data.page, limit: data.limit, pages: data.pages },
        historyStatusFilter: status ?? null,
        historyDateFrom: date_from ?? null,
        historyDateTo: date_to ?? null,
        historyLoading: false,
        historyError: null,
      });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Failed to load alert history";
      set({ historyLoading: false, historyError: message });
    }
  },

  setHistoryPage: (page) => {
    set((s) => ({ historyPagination: { ...s.historyPagination, page } }));
    const { historyPagination, historyStatusFilter, historyDateFrom, historyDateTo } = get();
    get().fetchAlertHistory({
      page,
      limit: historyPagination.limit,
      status: historyStatusFilter,
      date_from: historyDateFrom,
      date_to: historyDateTo,
    });
  },

  setHistoryFilters: (filters) => {
    const { historyStatusFilter, historyDateFrom, historyDateTo } = get();
    const status = filters.status !== undefined ? filters.status : historyStatusFilter;
    const date_from = filters.date_from !== undefined ? filters.date_from : historyDateFrom;
    const date_to = filters.date_to !== undefined ? filters.date_to : historyDateTo;
    set({ historyStatusFilter: status ?? null, historyDateFrom: date_from ?? null, historyDateTo: date_to ?? null });
    get().fetchAlertHistory({ page: 1, status, date_from, date_to });
  },
}));
